import { debounce, memoize } from 'lodash-es';

interface SyncScrollConf {
    el: HTMLElement | Window;
    /*
     * Calculates scroll distance fo the element.
     * Specify the scroll distance of the other element here if you want to match them fully
     */
    distance: () => { top?: number; left?: number };
}

/**
 * Memoized function that returns a debounced function that adds a scroll listener to the target element after an animation frame.
 * This is needed to always use the same debounced function for the same target.
 * Otherwise the debounce wouldn't work as expected.
 */
const addScrollListenerAfterAnimationFrameDebouncedTo = memoize((target: HTMLElement | Window) =>
    debounce(
        (listener: EventListener) =>
            window.requestAnimationFrame(() => {
                // eslint-disable-next-line tipsi/remove-event-listener -- listener is removed below
                target.addEventListener('scroll', listener);
            }),
        100,
    ),
);

function handleSyncedScroll({
    target,
    targetScrollListener,
    distance: { top, left },
}: {
    target: HTMLElement | Window;
    targetScrollListener: EventListener;
    distance: { top?: number; left?: number };
}) {
    target.removeEventListener('scroll', targetScrollListener);

    if (target instanceof Window) {
        target.scrollTo({
            top,
            left,
            behavior: 'instant',
        });
    } else {
        /* eslint-disable no-param-reassign */
        target.scrollTop = top ?? target.scrollTop;
        target.scrollLeft = left ?? target.scrollLeft;
        /* eslint-enable no-param-reassign */
    }

    addScrollListenerAfterAnimationFrameDebouncedTo(target)(targetScrollListener);
}

/**
 * Provides the foundation to syncronize the scrolling between 2 elements.
 * You can customize the scroll distance calculation for each element.
 *
 * @important Don't forget to unsync the scrolling afterwards!
 * The method to do that is part of the return.
 */
export function syncScroll(elem1: SyncScrollConf, elem2: SyncScrollConf) {
    const scrollHandler = {
        elem2: () =>
            handleSyncedScroll({
                target: elem2.el,
                targetScrollListener: scrollHandler.elem1,
                distance: elem2.distance(),
            }),
        elem1: () =>
            handleSyncedScroll({
                target: elem1.el,
                targetScrollListener: scrollHandler.elem2,
                distance: elem1.distance(),
            }),
    };

    elem1.el.addEventListener('scroll', scrollHandler.elem2);
    elem2.el.addEventListener('scroll', scrollHandler.elem1);

    return {
        resync: scrollHandler,
        unsync: () => {
            elem1.el.removeEventListener('scroll', scrollHandler.elem2);
            elem2.el.removeEventListener('scroll', scrollHandler.elem1);
        },
    };
}
